import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUser, FiMail, FiCheck } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay, rgba(0, 0, 0, 0.5));
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled(motion.div)`
  background-color: var(--surface-color, var(--background-primary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--control-radius, var(--card-radius));
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--background-secondary);
      color: var(--text-primary);
    }
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ModalSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
  margin: 0 0 2rem 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  color: rgba(220, 53, 69, 0.95);
  padding: 0.75rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  text-align: center;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(56, 161, 105, 0.08);
  border: 1px solid rgba(56, 161, 105, 0.25);
  color: rgba(56, 161, 105, 0.95);
  padding: 1rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  text-align: center;
`;

const EmailDisplay = styled.div`
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 1rem;
  margin-top: 1rem;
  text-align: center;
`;

const EmailText = styled.p`
  color: var(--text-primary);
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
`;

const BackToLogin = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  margin-top: 1rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--primary-color);
      color: var(--text-inverse);
    }
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

function ForgotUsernameModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [foundEmail, setFoundEmail] = useState('');

  const maskEmail = (email) => {
    if (!email || !email.includes('@')) {
      return email;
    }

    const [localPart, domain] = email.split('@');
    
    // Handle edge cases for very short local parts
    if (localPart.length === 1) {
      // If 1 character, show as-is with domain
      return `${localPart}@${domain}`;
    } else if (localPart.length === 2) {
      // If 2 characters, mask the 2nd character
      return `${localPart[0]}*@${domain}`;
    } else if (localPart.length === 3) {
      // For 3 characters: show first, mask middle, show last
      return `${localPart[0]}*${localPart[2]}@${domain}`;
    }

    // For 4+ characters: show first 2, mask middle, show last 1
    const firstTwo = localPart.substring(0, 2);
    const lastChar = localPart[localPart.length - 1];
    const middleLength = localPart.length - 3; // Subtract first 2 and last 1
    const maskedMiddle = '*'.repeat(middleLength);

    return `${firstTwo}${maskedMiddle}${lastChar}@${domain}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    setFoundEmail('');

    try {
      const response = await apiService.forgotUsername(formData.firstName, formData.lastName);
      
      if (response.success) {
        setSuccess(true);
        setFoundEmail(response.email);
      } else {
        setError(response.error || 'Account not found. Please contact admin@faithinsight.org for assistance.');
      }
    } catch (error) {
      logger.error('Forgot username error', { error, firstName: formData.firstName, lastName: formData.lastName });
      setError('Account not found. Please contact admin@faithinsight.org for assistance.');
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '' });
    setError('');
    setSuccess(false);
    setFoundEmail('');
    onClose();
  };

  const handleBackToLogin = () => {
    handleClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={handleClose} aria-label="Close modal">
              <FiX size={20} />
            </CloseButton>

            <ModalTitle>Forgot Username?</ModalTitle>
            <ModalSubtitle>
              Enter your first and last name to retrieve your email address.
            </ModalSubtitle>

            {!success ? (
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <InputWrapper>
                    <Icon>
                      <FiUser size={16} />
                    </Icon>
                    <Input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </InputWrapper>
                </FormGroup>

                <FormGroup>
                  <InputWrapper>
                    <Icon>
                      <FiUser size={16} />
                    </Icon>
                    <Input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </InputWrapper>
                </FormGroup>

                {error && (
                  <ErrorMessage
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    role="alert"
                  >
                    {error}
                  </ErrorMessage>
                )}

                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Searching...
                    </>
                  ) : (
                    <>
                      <FiMail size={16} />
                      Find My Email
                    </>
                  )}
                </Button>
              </Form>
            ) : (
              <div>
                <SuccessMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  role="alert"
                >
                  <FiCheck size={20} style={{ marginBottom: '0.5rem' }} />
                  <div>Account found!</div>
                </SuccessMessage>

                <EmailDisplay>
                  <EmailText>{maskEmail(foundEmail)}</EmailText>
                </EmailDisplay>

                <BackToLogin onClick={handleBackToLogin}>
                  Back to Login
                </BackToLogin>
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default ForgotUsernameModal;
